import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  DEFAULT_ALTA_USD,
  DEFAULT_EXTRA_SENSOR_USD,
  DEFAULT_INCLUDED_SENSORS,
  DEFAULT_MONTHLY_ABONO_USD,
  DEFAULT_SERVICE_TERM_MONTHS,
  legalPersonKindSchema,
  type LegalPersonKind,
  type ServiceAgreement,
} from "@/contracts";

import {
  createServiceAgreementMutation,
  loanAgreementsQuery,
  organizationOptionsQuery,
  siteOptionsQuery,
  updateServiceAgreementMutation,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { LEGAL_PERSON_LABELS } from "@/features/loans/loan-labels";

const formSchema = z.object({
  organizationId: z.string(),
  siteId: z.string().min(1),
  customerKind: legalPersonKindSchema,
  customerName: z.string().trim().min(2).max(200),
  customerTaxId: z.string().trim().min(1).max(40),
  customerAddress: z.string().trim().min(5).max(300),
  signatoryName: z.string().trim().min(2).max(200),
  signatoryDocument: z.string().trim().min(1).max(40),
  signatoryCapacity: z.string().trim().min(2).max(120),
  monthlyAbonoUsd: z.string().trim(),
  altaUsd: z.string().trim(),
  includedSensors: z.string().trim(),
  extraSensorUsd: z.string().trim(),
  termMonths: z.string().trim(),
  startsOn: z.string().min(1),
  loanAgreementId: z.string(),
  notes: z.string().trim().max(2_000),
});

type FormValues = z.infer<typeof formSchema>;

function valuesFromAgreement(agreement: ServiceAgreement): FormValues {
  return {
    organizationId: agreement.organizationId,
    siteId: agreement.siteId ?? "",
    customerKind: agreement.customerKind,
    customerName: agreement.customerName,
    customerTaxId: agreement.customerTaxId,
    customerAddress: agreement.customerAddress,
    signatoryName: agreement.signatoryName,
    signatoryDocument: agreement.signatoryDocument,
    signatoryCapacity: agreement.signatoryCapacity,
    monthlyAbonoUsd: String(agreement.monthlyAbonoUsd),
    altaUsd: String(agreement.altaUsd),
    includedSensors: String(agreement.includedSensors),
    extraSensorUsd:
      agreement.extraSensorUsd === null ? "" : String(agreement.extraSensorUsd),
    termMonths: String(agreement.termMonths),
    startsOn: agreement.startsOn.slice(0, 10),
    loanAgreementId: agreement.loanAgreementId ?? "",
    notes: agreement.notes ?? "",
  };
}

const emptyDefaults: FormValues = {
  organizationId: "",
  siteId: "",
  customerKind: "LEGAL_ENTITY",
  customerName: "",
  customerTaxId: "",
  customerAddress: "",
  signatoryName: "",
  signatoryDocument: "",
  signatoryCapacity: "",
  monthlyAbonoUsd: String(DEFAULT_MONTHLY_ABONO_USD),
  altaUsd: String(DEFAULT_ALTA_USD),
  includedSensors: String(DEFAULT_INCLUDED_SENSORS),
  extraSensorUsd: String(DEFAULT_EXTRA_SENSOR_USD),
  termMonths: String(DEFAULT_SERVICE_TERM_MONTHS),
  startsOn: new Date().toISOString().slice(0, 10),
  loanAgreementId: "",
  notes: "",
};

/** Values copied from a comodato when drawing up the paired monitoring fee. */
export interface ServiceAgreementPrefill {
  organizationId: string;
  siteId: string;
  siteName: string;
  loanAgreementId: string;
  loanAgreementNumber: string;
  customerKind: LegalPersonKind;
  customerName: string;
  customerTaxId: string;
  customerAddress: string;
  signatoryName: string;
  signatoryDocument: string;
  signatoryCapacity: string;
}

function valuesFromPrefill(prefill: ServiceAgreementPrefill): FormValues {
  return {
    ...emptyDefaults,
    organizationId: prefill.organizationId,
    siteId: prefill.siteId,
    loanAgreementId: prefill.loanAgreementId,
    customerKind: prefill.customerKind,
    customerName: prefill.customerName,
    customerTaxId: prefill.customerTaxId,
    customerAddress: prefill.customerAddress,
    signatoryName: prefill.signatoryName,
    signatoryDocument: prefill.signatoryDocument,
    signatoryCapacity: prefill.signatoryCapacity,
  };
}

export function ServiceAgreementCreateDialog({
  open,
  onClose,
  agreement,
  prefill,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** When set, the dialog edits this draft instead of creating a new one. */
  agreement?: ServiceAgreement;
  /** When set (create only), party and site come from a linked comodato. */
  prefill?: ServiceAgreementPrefill;
  /**
   * When set, called instead of navigating to the new service detail page
   * (e.g. stay in the onboarding wizard).
   */
  onCreated?: (agreement: ServiceAgreement) => void;
}) {
  const { t } = useTranslation("services");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const editing = agreement !== undefined;
  const fromLoan = !editing && prefill !== undefined;
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({
    ...organizationOptionsQuery(client),
    enabled: isSuperAdmin && !editing && !fromLoan && open,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyDefaults,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (agreement) {
      reset(valuesFromAgreement(agreement));
      return;
    }

    reset(prefill ? valuesFromPrefill(prefill) : emptyDefaults);
  }, [open, agreement, prefill, reset]);

  const organizationId = useWatch({ control, name: "organizationId" });
  const siteId = useWatch({ control, name: "siteId" });
  const customerKind = useWatch({ control, name: "customerKind" });
  const loanAgreementId = useWatch({ control, name: "loanAgreementId" });

  const tenantOrgId = editing
    ? agreement.organizationId
    : fromLoan
      ? prefill.organizationId
      : isSuperAdmin
        ? organizationId || undefined
        : undefined;

  const sites = useQuery({
    ...siteOptionsQuery(client, tenantOrgId),
    enabled: open && !editing && !fromLoan && (!isSuperAdmin || (organizationId?.length ?? 0) > 0),
  });
  const loans = useQuery({
    ...loanAgreementsQuery(client, {
      page: 1,
      ...(tenantOrgId ? { organizationId: tenantOrgId } : {}),
    }),
    enabled:
      open &&
      !fromLoan &&
      (!isSuperAdmin || editing || (organizationId?.length ?? 0) > 0),
  });

  const createMutation = useMutation({
    ...createServiceAgreementMutation(client),
    onSuccess: async (created) => {
      reset();
      onClose();
      await queryClient.invalidateQueries({ queryKey: ["service-agreements"] });
      if (onCreated) {
        onCreated(created);
        return;
      }
      await navigate(`/service-agreements/${created.id}`);
    },
  });

  const updateMutation = useMutation({
    ...updateServiceAgreementMutation(client),
    onSuccess: async () => {
      reset();
      onClose();
      await queryClient.invalidateQueries({ queryKey: ["service-agreements"] });
    },
  });

  const mutation = editing ? updateMutation : createMutation;

  const onSubmit = handleSubmit((values) => {
    if (editing) {
      updateMutation.mutate({
        id: agreement.id,
        body: {
          customerKind: values.customerKind,
          customerName: values.customerName,
          customerTaxId: values.customerTaxId,
          customerAddress: values.customerAddress,
          signatoryName: values.signatoryName,
          signatoryDocument: values.signatoryDocument,
          signatoryCapacity: values.signatoryCapacity,
          monthlyAbonoUsd: Number(values.monthlyAbonoUsd),
          altaUsd: Number(values.altaUsd),
          includedSensors: Number(values.includedSensors),
          extraSensorUsd: values.extraSensorUsd.trim() ? Number(values.extraSensorUsd) : null,
          termMonths: Number(values.termMonths),
          startsOn: values.startsOn,
          loanAgreementId: values.loanAgreementId || null,
          notes: values.notes,
        },
      });
      return;
    }

    createMutation.mutate({
      ...(isSuperAdmin && values.organizationId ? { organizationId: values.organizationId } : {}),
      siteId: values.siteId,
      customerKind: values.customerKind,
      customerName: values.customerName,
      customerTaxId: values.customerTaxId,
      customerAddress: values.customerAddress,
      signatoryName: values.signatoryName,
      signatoryDocument: values.signatoryDocument,
      signatoryCapacity: values.signatoryCapacity,
      monthlyAbonoUsd: Number(values.monthlyAbonoUsd),
      altaUsd: Number(values.altaUsd),
      includedSensors: Number(values.includedSensors),
      extraSensorUsd: values.extraSensorUsd.trim() ? Number(values.extraSensorUsd) : null,
      termMonths: Number(values.termMonths),
      startsOn: values.startsOn,
      loanAgreementId: values.loanAgreementId || null,
      ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
    });
  });

  return (
    <FormDialog
      open={open}
      title={editing ? t("editTitle") : fromLoan ? t("createFromLoanTitle") : t("createTitle")}
      {...(editing
        ? { description: t("editDescription") }
        : fromLoan
          ? { description: t("createFromLoanDescription") }
          : {})}
      submitLabel={editing ? t("editSubmit") : t("createSubmit")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={() => {
        reset();
        onClose();
      }}
    >
      <div className="flex flex-col gap-4">
        {mutation.error ? <Alert>{mutation.error.message}</Alert> : null}

        {editing ? (
          <p className="text-sm text-ink-muted">
            {t("editSiteLocked", { site: agreement.siteName })}
          </p>
        ) : fromLoan ? (
          <Alert tone="info" title={t("createFromLoanLinkTitle")} announce={false}>
            {t("createFromLoanLinkBody", {
              loan: prefill.loanAgreementNumber,
              site: prefill.siteName,
            })}
          </Alert>
        ) : (
          <>
            {isSuperAdmin ? (
              <SelectField
                label={tCommon("organization")}
                required
                value={organizationId}
                onChange={(value) => {
                  setValue("organizationId", value, { shouldValidate: true });
                  setValue("siteId", "");
                  setValue("loanAgreementId", "");
                }}
                data={(organizations.data?.data ?? []).map((organization) => ({
                  value: organization.id,
                  label: organization.name,
                }))}
              />
            ) : null}

            <SelectField
              label={t("colSite")}
              required
              value={siteId}
              onChange={(value) => setValue("siteId", value, { shouldValidate: true })}
              data={(sites.data?.data ?? []).map((site) => ({ value: site.id, label: site.name }))}
            />
          </>
        )}

        <SelectField
          label={t("fieldCustomerKind")}
          value={customerKind}
          onChange={(value) =>
            setValue("customerKind", value as FormValues["customerKind"], {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          data={Object.entries(LEGAL_PERSON_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
        />

        <TextField
          label={t("fieldCustomerName")}
          required
          {...register("customerName")}
          {...(errors.customerName?.message ? { error: errors.customerName.message } : {})}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t("fieldCustomerTaxId")} required {...register("customerTaxId")} />
          <TextField label={t("fieldCustomerAddress")} required {...register("customerAddress")} />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField label={t("fieldSignatoryName")} required {...register("signatoryName")} />
          <TextField
            label={t("fieldSignatoryDocument")}
            required
            {...register("signatoryDocument")}
          />
          <TextField
            label={t("fieldSignatoryCapacity")}
            required
            {...register("signatoryCapacity")}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("fieldMonthlyAbono")}
            required
            type="number"
            step="0.01"
            {...register("monthlyAbonoUsd")}
          />
          <TextField
            label={t("fieldAlta")}
            required
            type="number"
            step="0.01"
            {...register("altaUsd")}
          />
          <TextField
            label={t("fieldIncludedSensors")}
            required
            type="number"
            {...register("includedSensors")}
          />
          <TextField
            label={t("fieldExtraSensor")}
            type="number"
            step="0.01"
            {...register("extraSensorUsd")}
          />
          <TextField label={t("fieldTermMonths")} required type="number" {...register("termMonths")} />
          <TextField label={t("fieldStartsOn")} required type="date" {...register("startsOn")} />
        </div>

        {fromLoan ? null : (
          <SelectField
            label={t("fieldLoanAgreement")}
            value={loanAgreementId}
            onChange={(value) => setValue("loanAgreementId", value)}
            data={[
              { value: "", label: t("noLoanLink") },
              ...(loans.data?.data ?? []).map((loan) => ({
                value: loan.id,
                label: `${loan.number} — ${loan.siteName}`,
              })),
            ]}
          />
        )}

        <TextAreaField label={t("fieldNotes")} {...register("notes")} />
      </div>
    </FormDialog>
  );
}
